import {
    DICTIONARY_INFO, FIELD_INFO, LANGUAGE_INFO, LANGUAGE_KEYS, RULE_PROVIDER_INWORLD, FALLBACK_AUTO_PROVIDERS
} from '../../Mobx/AudioCatalog';

/**
 * The form of the rules of the automatic creation (the "כללי יצירה אוטומטית" screen).
 * The rules decide, for each dictionary and each language, whether the audio file is created when an entry is added or changed,
 * and by which provider: { enabled, rules: { heb_to_eng: { he: { enabled, provider }, en_US: ..., }, eng_to_heb: ... } }.
 * These functions convert the settings of the server (GET /api/ai/settings/audio, the part "auto") to the form,
 * decide what has to be saved, and check the form before it is sent. No React here, so they are easy to test.
 */

const CELL_FIELDS = ['enabled', 'provider'];

/** The keys of the dictionaries, in the order of the server */
export const dictionaryKeysOf = (autoView) => (autoView.catalog.dictionaries?.length
    ? autoView.catalog.dictionaries.map((dictionary) => dictionary.key)
    : Object.keys(autoView.effective.rules));

/** The form shows what the automatic creation really uses (effective), not the raw saved values */
export const formFromAutoView = (autoView) => {
    const { effective } = autoView;
    const form = { enabled: effective.enabled, rules: {} };
    for (const dictionary of dictionaryKeysOf(autoView)) {
        form.rules[dictionary] = {};
        for (const language of LANGUAGE_KEYS) {
            const cell = effective.rules?.[dictionary]?.[language] || {};
            form.rules[dictionary][language] = { enabled: Boolean(cell.enabled), provider: cell.provider };
        }
    }
    return form;
};

/** A field is saved when the user changed it, or when the saved value is not valid (then it is replaced by the value of the form) */
const needsSave = (savedValue, effectiveValue, formValue) =>
    formValue !== effectiveValue || (savedValue !== undefined && savedValue !== effectiveValue);

/**
 * What to send to the server (PATCH /api/ai/settings/audio/auto): { enabled, rules: { heb_to_eng: { he: { provider } } } }
 * Only the fields that have to be saved. {} - nothing to save.
 */
export const buildAutoPatch = (form, autoView) => {
    const configured = autoView.configured || {};
    const { effective } = autoView;
    const patch = {};

    if (needsSave(configured.enabled, effective.enabled, form.enabled)) patch.enabled = form.enabled;

    const rules = {};
    for (const dictionary of Object.keys(form.rules)) {
        for (const language of LANGUAGE_KEYS) {
            const saved = configured.rules?.[dictionary]?.[language] || {};
            const current = effective.rules?.[dictionary]?.[language] || {};
            const cell = form.rules[dictionary][language];
            const changes = {};
            for (const field of CELL_FIELDS) {
                if (needsSave(saved[field], current[field], cell[field])) changes[field] = cell[field];
            }
            if (Object.keys(changes).length > 0) {
                rules[dictionary] = rules[dictionary] || {};
                rules[dictionary][language] = changes;
            }
        }
    }
    if (Object.keys(rules).length > 0) patch.rules = rules;
    return patch;
};

/** ['enabled', 'heb_to_eng.he.provider'] - the names of the fields in a patch, to show the user what is going to change */
export const autoPatchPaths = (patch) => {
    const paths = [];
    if ('enabled' in patch) paths.push('enabled');
    for (const [dictionary, languages] of Object.entries(patch.rules || {})) {
        for (const [language, cell] of Object.entries(languages)) {
            for (const field of Object.keys(cell)) paths.push(`${dictionary}.${language}.${field}`);
        }
    }
    return paths;
};

/** The providers that can be chosen in a rule, as the server sends them */
export const providersOf = (autoView) => (autoView.catalog.providers?.length ? autoView.catalog.providers : FALLBACK_AUTO_PROVIDERS);

/** 'Google (Gemini)' - how a provider is written */
export const providerLabelOf = (autoView, id) => providersOf(autoView).find((provider) => provider.id === id)?.label || id;

/**
 * Inworld can be chosen only while the server has its API key - otherwise every new entry would fail (the server refuses the save).
 * A cell that already uses Inworld keeps it, so the screen does not break when the key is removed.
 */
export const isProviderUnavailable = (providerId, capabilities, currentProviderId) =>
    providerId === RULE_PROVIDER_INWORLD && capabilities?.inworldApiKey === false && currentProviderId !== RULE_PROVIDER_INWORLD;

/** @returns {string[]} what is wrong in the form (Hebrew). [] - the form can be saved. */
export const validateAutoForm = (form, view) => {
    const problems = [];
    const autoView = view.auto;
    const providers = providersOf(autoView).map((provider) => provider.id);
    for (const dictionary of Object.keys(form.rules)) {
        const dictionaryLabel = DICTIONARY_INFO[dictionary]?.label || dictionary;
        for (const language of LANGUAGE_KEYS) {
            const cell = form.rules[dictionary][language];
            const where = `${dictionaryLabel}, ${LANGUAGE_INFO[language].label}`;
            if (!providers.includes(cell.provider)) {
                problems.push(`${where}: לא נבחר ספק.`);
                continue;
            }
            const current = autoView.effective.rules?.[dictionary]?.[language]?.provider;
            if (isProviderUnavailable(cell.provider, view.capabilities, current)) {
                problems.push(`${where}: אי אפשר לבחור ב-Inworld, כי לשרת אין מפתח Inworld.`);
            }
        }
    }
    return problems;
};

/** true - the user changed something in the form that was not saved */
export const isAutoFormChanged = (form, autoView) => JSON.stringify(form) !== JSON.stringify(formFromAutoView(autoView));

/**
 * What a dictionary row says about the field that is spoken: for the dictionary and the language, the fields of the entry
 * that become audio in this language (the server sends the map in the catalog).
 * @returns {string} for example "התרגום" - or '' when the catalog does not say
 */
export const fieldLabelOf = (dictionaryCatalog, language) => {
    const fields = dictionaryCatalog?.fields || {};
    const names = Object.keys(fields).filter((field) => fields[field].includes(language));
    return names.map((field) => FIELD_INFO[field] || field).join(', ');
};

/** How many cells are on, and how many of them use each provider: { total, enabled, byProvider: { google: 3, inworld: 2 } } */
export const summarizeRules = (form) => {
    const summary = { total: 0, enabled: 0, byProvider: {} };
    for (const dictionary of Object.keys(form.rules)) {
        for (const language of LANGUAGE_KEYS) {
            const cell = form.rules[dictionary][language];
            summary.total += 1;
            if (cell.enabled) {
                summary.enabled += 1;
                summary.byProvider[cell.provider] = (summary.byProvider[cell.provider] || 0) + 1;
            }
        }
    }
    return summary;
};
